// Cuentas CMF conocidas: lista compartida entre Banco CMF > Movimientos y Saldos.
//
// La fuente es GET /cmf/cuentas (banco + etiquetas guardadas en la tabla settings
// + seed de la recaudadora). Antes era una constante hardcodeada acá en el
// frontend; desde 2026-09-05 la etiqueta la edita el operador (lapicito en el
// título de Movimientos) y queda del lado del servidor.
//
// Cache a nivel módulo con suscriptores: las dos páginas comparten una sola
// carga y un `saveLabel` en una refresca a la otra. Si el backend no responde,
// se usa el fallback offline (sólo la recaudadora, que es la única con
// histórico local) para que la página siga operable.
import { useCallback, useEffect, useState } from "react";
import {
  getCmfCuentas, setCmfCuentaLabel, CMF_CUENTA_DEFAULT, type CmfCuenta,
} from "./cmf";

export const CMF_CUENTAS_FALLBACK: CmfCuenta[] = [
  { numero: CMF_CUENTA_DEFAULT, label: "Recaudadora", hist: true, source: "seed" },
];

let cache: CmfCuenta[] = CMF_CUENTAS_FALLBACK;
let loaded = false;
let inflight: Promise<CmfCuenta[]> | null = null;
const subs = new Set<(c: CmfCuenta[]) => void>();

function publish(next: CmfCuenta[]) {
  cache = next;
  loaded = true;
  subs.forEach((fn) => fn(next));
}

async function load(force = false): Promise<CmfCuenta[]> {
  if (loaded && !force) return cache;
  if (inflight) return inflight;
  inflight = getCmfCuentas()
    .then((r) => { publish(r.cuentas.length ? r.cuentas : CMF_CUENTAS_FALLBACK); return cache; })
    .catch(() => { publish(cache); return cache; })   // offline: seguimos con lo que hay
    .finally(() => { inflight = null; });
  return inflight;
}

export function useCmfAccounts() {
  const [cuentas, setCuentas] = useState<CmfCuenta[]>(cache);
  const [ready, setReady] = useState(loaded);

  useEffect(() => {
    const fn = (c: CmfCuenta[]) => { setCuentas(c); setReady(true); };
    subs.add(fn);
    load().then(fn);
    return () => { subs.delete(fn); };
  }, []);

  const reload = useCallback(() => load(true), []);

  // Guarda la etiqueta (vacío = borra) y refresca la lista compartida.
  const saveLabel = useCallback(async (numero: string, label: string) => {
    const r = await setCmfCuentaLabel(numero, label);
    await load(true);
    return r;
  }, []);

  const find = useCallback((numero: string) => cuentas.find((c) => c.numero === numero), [cuentas]);
  const label = useCallback((numero: string): string | undefined => find(numero)?.label ?? undefined, [find]);
  const hasHist = useCallback((numero: string): boolean => !!find(numero)?.hist, [find]);

  return { cuentas, ready, reload, saveLabel, find, label, hasHist };
}
